import { apiSlice } from "../createApiSlice";

const PWA_BASE = "/pwa";
const buildUrl = (endpoint) => PWA_BASE + endpoint;

const pwaSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        
        // ===================
        // VAPID KEYS
        // ===================
        getVapidPublicKey: builder.query({
            query: () => ({
                url: buildUrl('/vapid/public-key'),
                method: 'GET',
            }),
            providesTags: ['VapidKeys'],
            transformResponse: (response) => response.data?.publicKey || response.publicKey,
        }),

        // ===================
        // PUSH SUBSCRIPTIONS
        // ===================
        subscribeToPush: builder.mutation({
            query: ({ userId, subscription, userAgent, deviceInfo }) => ({
                url: buildUrl('/push/subscribe'),
                method: 'POST',
                body: {
                    userId,
                    subscription,
                    userAgent,
                    deviceInfo: {
                        platform: navigator.platform,
                        language: navigator.language,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        ...deviceInfo
                    }
                },
            }),
            invalidatesTags: ['PushSubscriptions'],
            // transformResponse: (response) => response.data || response,
        }),

        unsubscribeFromPush: builder.mutation({
            query: ({ userId, endpoint }) => ({
                url: buildUrl('/push/unsubscribe'),
                method: 'DELETE',
                body: { userId, endpoint },
            }),
            invalidatesTags: ['PushSubscriptions'],
            // transformResponse: (response) => response.data || response,
        }),

        getUserSubscriptions: builder.query({
            query: () => ({
                url: buildUrl(`/push/subscriptions`),
                method: 'GET',
            }),
            providesTags: (result, error, userId) => [
                { type: 'PushSubscriptions' }
            ],
            // transformResponse: (response) => response.data || response,
        }),

        updateSubscription: builder.mutation({
            query: ({ subscriptionId, isActive }) => ({
                url: buildUrl(`/push/subscription/${subscriptionId}`),
                method: 'PATCH',
                body: { isActive },
            }),
            invalidatesTags: ['PushSubscriptions'],
            // transformResponse: (response) => response.data || response,
        }),

        // ===================
        // NOTIFICATION PREFERENCES
        // ===================
        getNotificationPreferences: builder.query({
            query: () => ({
                url: buildUrl(`/notifications/preferences`),
                method: 'GET',
            }),
            providesTags: (result, error, userId) => [
                { type: 'NotificationPreferences', id: userId }
            ],
            // transformResponse: (response) => response.data || response,
        }),

        updateNotificationPreferences: builder.mutation({
            query: (preferences) => ({
                url: buildUrl(`/notifications/preferences/`),
                method: 'PUT',
                body: preferences,
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'NotificationPreferences', id: userId }
            ],
            // transformResponse: (response) => response.data || response,
        }),

        resetNotificationPreferences: builder.mutation({
            query: (userId) => ({
                url: buildUrl(`/notifications/preferences/${userId}/reset`),
                method: 'POST',
            }),
            invalidatesTags: (result, error, userId) => [
                { type: 'NotificationPreferences', id: userId }
            ],
            // transformResponse: (response) => response.data || response,
        }),

        // ===================
        // NOTIFICATION HISTORY
        // ===================
        getNotificationHistory: builder.query({
            query: ({  page = 1, limit = 100, type, unreadOnly = false }) => {
                const params = new URLSearchParams({
                    page: page.toString(),
                    limit: limit.toString(),
                    ...(type && { type }),
                    ...(unreadOnly && { unreadOnly: unreadOnly })
                });
                return {
                    url: buildUrl(`/notifications/history?${params}`),
                    method: 'GET',
                };
            },
            providesTags: (result, error, { userId }) => [
                { type: 'NotificationHistory', id: userId },
                { type: 'NotificationHistory', id: 'LIST' }
            ],
        }),
        getNotifcationsModal: builder.query({
            query: () => {
                return {
                    url: buildUrl(`/notifications/modal`),
                    method: 'GET',
                };
            },
            providesTags: (result, error) => [
                { type: 'NotificationModal', }
            ],
            // transformResponse: (response) => response.data || response,
        }),
        markNotificationAsRead: builder.mutation({
            query: ({ notificationId, userId }) => ({
                url: buildUrl(`/notifications/${notificationId}/read`),
                method: 'PATCH',
                body: { userId },
            }),
            invalidatesTags: ['NotificationHistory', 'NotificationModal'],
            // transformResponse: (response) => response.data || response,
        }),

        markAllNotificationsAsRead: builder.mutation({
            query: () => ({
                url: buildUrl('/notifications/mark-all-read'),
                method: 'POST',
            }),
            invalidatesTags: ['NotificationHistory', 'NotificationModal'],
            // transformResponse: (response) => response.data || response,
        }),

        deleteNotification: builder.mutation({
            query: ({ notificationId, userId }) => ({
                url: buildUrl(`/notifications/${notificationId}`),
                method: 'DELETE',
                body: { userId },
            }),
            invalidatesTags: ['NotificationHistory', 'NotificationModal'],
            // transformResponse: (response) => response.data || response,
        }),

        getUnreadNotificationCount: builder.query({
            query: (userId) => ({
                url: buildUrl(`/notifications/unread-count`),
                method: 'GET',
            }),
            providesTags: (result, error) => [
                { type: 'NotificationHistory' }
            ],
            transformResponse: (response) => response.data?.count !== undefined ? response.data.count : response.count,
        }),

        // ===================
        // NOTIFICATION SENDING
        // ===================
        sendTestNotification: builder.mutation({
            query: (notificationType) => ({
                url: buildUrl('/notifications/test'),
                method: 'POST',
                body: notificationType,
            }),
            invalidatesTags: ['NotificationHistory', 'NotificationModal'],
            // transformResponse: (response) => response.data || response,
        }),

        sendNotificationToUser: builder.mutation({
            query: ({ 
                userId, 
                title, 
                body, 
                type, 
                data, 
                urgent = false,
                scheduleFor = null 
            }) => ({
                url: buildUrl('/notifications/send'),
                method: 'POST',
                body: {
                    userId,
                    title,
                    body,
                    type,
                    data,
                    urgent,
                    scheduleFor
                },
            }),
            invalidatesTags: ['NotificationHistory', 'NotificationModal'],
            transformResponse: (response) => response.data || response,
        }),

        sendBulkNotifications: builder.mutation({
            query: ({ 
                userIds, 
                title, 
                body, 
                type, 
                data, 
                urgent = false,
                filterByPreferences = true 
            }) => ({
                url: buildUrl('/notifications/send-bulk'),
                method: 'POST',
                body: {
                    userIds,
                    title,
                    body,
                    type,
                    data,
                    urgent,
                    filterByPreferences
                },
            }),
            invalidatesTags: ['NotificationHistory', 'NotificationModal'],
            transformResponse: (response) => response.data || response,
        }),

        // ===================
        // NOTIFICATION ANALYTICS
        // ===================
        getNotificationStats: builder.query({
            query: ({ userId, dateFrom, dateTo, type }) => {
                const params = new URLSearchParams({
                    ...(dateFrom && { dateFrom }),
                    ...(dateTo && { dateTo }),
                    ...(type && { type })
                });
                return {
                    url: buildUrl(`/notifications/stats/${userId}?${params}`),
                    method: 'GET',
                };
            },
            providesTags: (result, error, { userId }) => [
                { type: 'NotificationStats', id: `stats-${userId}` }
            ],
            transformResponse: (response) => response.data || response,
        }),

        trackNotificationInteraction: builder.mutation({
            query: ({ notificationId, userId, action, timestamp = new Date().toISOString() }) => ({
                url: buildUrl(`/notifications/${notificationId}/track`),
                method: 'POST',
                body: { userId, action, timestamp },
            }),
            transformResponse: (response) => response.data || response,
        }),

        // ===================
        // DEVICE MANAGEMENT
        // ===================
        getUserDevices: builder.query({
            query: (userId) => ({
                url: buildUrl(`/devices/${userId}`),
                method: 'GET',
            }),
            providesTags: (result, error, userId) => [
                { type: 'UserDevices', id: `devices-${userId}` }
            ],
            transformResponse: (response) => response.data || response,
        }),

        updateDeviceName: builder.mutation({
            query: ({ subscriptionId, deviceName }) => ({
                url: buildUrl(`/devices/${subscriptionId}/name`),
                method: 'PATCH',
                body: { deviceName },
            }),
            invalidatesTags: ['UserDevices'],
            transformResponse: (response) => response.data || response,
        }),

        removeDevice: builder.mutation({
            query: ({ subscriptionId, userId }) => ({
                url: buildUrl(`/devices/${subscriptionId}`),
                method: 'DELETE',
                body: { userId },
            }),
            invalidatesTags: ['UserDevices'],
            transformResponse: (response) => response.data || response,
        }),

        // ===================
        // NOTIFICATION TEMPLATES
        // ===================
        getNotificationTemplates: builder.query({
            query: ({ type, language = 'en' }) => {
                const params = new URLSearchParams({
                    language,
                    ...(type && { type })
                });
                return {
                    url: buildUrl(`/notifications/templates?${params}`),
                    method: 'GET',
                };
            },
            providesTags: ['NotificationTemplates'],
            transformResponse: (response) => response.data || response,
        }),

        // ===================
        // SCHEDULED NOTIFICATIONS
        // ===================
        getScheduledNotifications: builder.query({
            query: ({ userId, page = 1, limit = 20 }) => {
                const params = new URLSearchParams({
                    page: page.toString(),
                    limit: limit.toString()
                });
                return {
                    url: buildUrl(`/notifications/scheduled/${userId}?${params}`),
                    method: 'GET',
                };
            },
            providesTags: (result, error, { userId }) => [
                { type: 'ScheduledNotifications', id: `scheduled-${userId}` }
            ],
            transformResponse: (response) => response.data || response,
        }),

        cancelScheduledNotification: builder.mutation({
            query: ({ notificationId, userId }) => ({
                url: buildUrl(`/notifications/scheduled/${notificationId}/cancel`),
                method: 'DELETE',
                body: { userId },
            }),
            invalidatesTags: (result, error, { userId }) => [
                { type: 'ScheduledNotifications', id: `scheduled-${userId}` }
            ],
            transformResponse: (response) => response.data || response,
        }),
    }),
});

// Export hooks for usage in functional components
export const {
    // VAPID Keys
    useGetVapidPublicKeyQuery,
    
    // Push Subscriptions
    useSubscribeToPushMutation,
    useUnsubscribeFromPushMutation,
    useGetUserSubscriptionsQuery,
    useUpdateSubscriptionMutation,
    
    // Notification Preferences
    useGetNotificationPreferencesQuery,
    useUpdateNotificationPreferencesMutation,
    useResetNotificationPreferencesMutation,
    
    // Notification History
    useGetNotifcationsModalQuery,
    useGetNotificationHistoryQuery,
    useMarkNotificationAsReadMutation,
    useMarkAllNotificationsAsReadMutation,
    useDeleteNotificationMutation,
    useGetUnreadNotificationCountQuery,
    
    // Notification Sending
    useSendTestNotificationMutation,
    useSendNotificationToUserMutation,
    useSendBulkNotificationsMutation,
    
    // Analytics
    useGetNotificationStatsQuery,
    useTrackNotificationInteractionMutation,
    
    // Device Management
    useGetUserDevicesQuery,
    useUpdateDeviceNameMutation,
    useRemoveDeviceMutation,
    
    // Templates
    useGetNotificationTemplatesQuery,
    
    // Scheduled Notifications
    useGetScheduledNotificationsQuery,
    useCancelScheduledNotificationMutation,
} = pwaSlice;

export default pwaSlice; 